import { auth } from '@jaymathew/ui-shared/apis/FirebaseUtilities/FirebaseConfig'
import { useQuery } from '@tanstack/react-query'
import { onIdTokenChanged } from 'firebase/auth'
import { useEffect } from 'react'

// custom claim keys that are NOT company IDs
const RESERVED_KEYS = new Set([
	'aud', 'auth_time', 'email', 'email_verified', 'exp', 'firebase',
	'iat', 'iss', 'name', 'sub', 'user_id', 'uid',
])

export type CompanyMembership = {
	companyId: string
	role: string
}

export function useUserCompanyIds(uid: string | undefined) {
	const q = useQuery({
		queryKey: ['claims.companyMemberships', uid],
		enabled: !!uid,
		queryFn: async (): Promise<CompanyMembership[]> => {
			const user = auth.currentUser
			if (!user) return []

			// make sure we have fresh claims
			await user.getIdToken(true)
			const tr = await user.getIdTokenResult()
			const claims = tr.claims as Record<string, unknown>

			const memberships: CompanyMembership[] = []
			for (const [key, val] of Object.entries(claims)) {
				if (RESERVED_KEYS.has(key)) continue
				if (typeof val !== 'string') continue // only accept string roles
				memberships.push({ companyId: key, role: val })
			}

			return memberships
		},
		staleTime: 5 * 60 * 1000,
	})

	// keep fresh when the ID token changes (token refresh / sign-out / cross-tab)
	useEffect(() => {
		if (!uid) return
		const unsub = onIdTokenChanged(auth, () => q.refetch())
		return () => unsub()
	}, [uid, q])

	return q
}
